/*

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import {
  Content,
  Page,
  Tab,
  TableColumn,
  TableProps,
} from '@backstage/core-components';
import { CatalogTableRow, catalogPlugin } from '@backstage/plugin-catalog';
import {
  EntityListProvider,
  EntityOwnerPicker,
  EntityTagPicker,
  EntityTypePicker,
  UserListFilterKind,
  EntityKindPicker,
  EntityOwnerPickerProps,
} from '@backstage/plugin-catalog-react';
import { ReactNode, useMemo, useState } from 'react';
import { Box, Grid } from '@material-ui/core';
import { UserListPicker } from './filters/userListPicker/UserListPicker';
import { Entity } from '@backstage/catalog-model';
import { DomainTabs } from './catalogByCategory/DomainTabs';
import {
  ParentEntityRef,
  useCatalogHierarchy,
} from './catalogByCategory/useCatalogHierarchy';
import SystemTreeNav from './catalogByCategory/SystemTreeNav';
import { CatalogList } from './CatalogList';
import { useRouteRef } from '@backstage/core-plugin-api';

/**
 * Props for root catalog pages.
 *
 * @public
 */
export interface CatalogPageProps {
  initiallySelectedFilter?: UserListFilterKind;
  columns?: TableColumn<CatalogTableRow>[];
  actions?: TableProps<CatalogTableRow>['actions'];
  initialKind?: string;
  lifecycle?: string[];
  tableOptions?: TableProps<CatalogTableRow>['options'];
  emptyContent?: ReactNode;
  ownerPickerMode?: EntityOwnerPickerProps['mode'];
}

export type EntityParent = Entity & { childEntities: Entity[] };

export function CatalogPage(props: CatalogPageProps) {
  const {
    initiallySelectedFilter = 'all',
    initialKind = 'component',
    ownerPickerMode,
  } = props;

  const [selectedDomainTabIndex, setSelectedDomainTabIndex] = useState(0);
  const { catalogHierarchy } = useCatalogHierarchy();
  const rootRoute = useRouteRef(catalogPlugin.routes.catalogIndex);

  const tabs: Tab[] = useMemo(() => {
    return catalogHierarchy.map((item: ParentEntityRef) => {
      return {
        id: item.name,
        label: item.title ?? item.name,
      };
    });
  }, [catalogHierarchy]);

  const onTabChange = (tabIndex: number) => {
    setSelectedDomainTabIndex(tabIndex);
  };

  return (
    <Page themeId="home">
      <DomainTabs
        rootRoute={rootRoute()}
        tabs={tabs}
        selectedIndex={selectedDomainTabIndex}
        onChange={onTabChange} 
      />
      <EntityListProvider>
        {/* <Header title="Catalog">
          { <Box mr={1}>
            <CatalogFilterLayout.Filters
              options={{ drawerBreakpoint: 'xl', drawerAnchor: 'right' }}
            >
              
            </CatalogFilterLayout.Filters>
          </Box>}
        </Header> */}
        <Content>
          <Box style={{ display: 'none' }}>
                <EntityKindPicker
                  initialFilter={initialKind}
                  allowedKinds={['Component', 'API', 'System', 'Domain', 'Resource']}
                  hidden
                />
                <EntityTypePicker />
                <UserListPicker initialFilter={initiallySelectedFilter} />
                <EntityOwnerPicker mode={ownerPickerMode} />
                <EntityTagPicker />
              </Box>
          <Grid container style={{ position: 'relative' }}>
            {catalogHierarchy && catalogHierarchy.length > 0 && (
              <Grid item lg={3} style={{ display: 'none' }}>
                <SystemTreeNav
                  entities={
                    catalogHierarchy[selectedDomainTabIndex].children ?? []
                  }
                />
              </Grid>
            )}
            <Grid item xs={12} lg={9}>
              <CatalogList />
            </Grid>
          </Grid>
        </Content>
      </EntityListProvider>
    </Page>
  );
}
